import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { TextB, TextItalic, TextUnderline, TextH, ListBullets, ListNumbers, PaintBrush, FloppyDisk, Eye, ArrowsClockwise, Sparkle, TextAa, Trash } from "@phosphor-icons/react";
import { UploadTemplateButton, CtcTableDialog } from "../components/LetterTools";

// Kind → readable title
const KIND_LABELS = {
  offer_letter: "Offer letter",
  appointment_letter: "Appointment letter",
  confirmation: "Confirmation letter",
  experience_letter: "Experience certificate",
  relieving_letter: "Relieving letter",
  salary_revision: "Salary revision letter",
  promotion: "Promotion letter",
  warning: "Warning letter",
  pip: "Performance improvement plan",
  no_dues: "No-dues certificate",
  transfer: "Transfer order",
  internship: "Internship letter",
};

const AUTO_VARS = [
  { key: "employee_name", desc: "Full name" },
  { key: "employee_code", desc: "ACM-123" },
  { key: "designation", desc: "Job title" },
  { key: "department", desc: "Department" },
  { key: "grade", desc: "Grade" },
  { key: "band", desc: "Band" },
  { key: "location", desc: "Office / branch" },
  { key: "joining_date", desc: "DOJ" },
  { key: "tenant_name", desc: "Company name" },
  { key: "today", desc: "Today's date" },
  { key: "current_ctc", desc: "Annual CTC" },
  { key: "ctc_annual", desc: "Annual CTC (INR)" },
];

const CUSTOM_VARS = [
  { key: "annual_ctc", desc: "Annual CTC used by {{ ctc_table }}" },
  { key: "new_ctc", desc: "New annual CTC" },
  { key: "old_ctc", desc: "Previous CTC" },
  { key: "percent_hike", desc: "Increment %" },
  { key: "effective_date", desc: "Effective date" },
  { key: "manager_name", desc: "Reporting manager" },
];

export default function LetterStudio() {
  const { user, tenantId } = useAuth();
  const [kind, setKind] = useState("salary_revision");
  const [title, setTitle] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [previewEmpId, setPreviewEmpId] = useState("");
  const [preview, setPreview] = useState(null);
  const [customVars, setCustomVars] = useState([{ k: "new_ctc", v: "₹ 18,00,000" }, { k: "effective_date", v: "01 April 2026" }]);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  const params = useMemo(() => (user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {}), [user?.role, tenantId]);

  // Load templates + employees
  const loadAll = async () => {
    try {
      const [tpls, emps] = await Promise.all([
        api.get("/letter-templates", { params }).then((r) => r.data).catch(() => []),
        api.get("/employees", { params }).then((r) => r.data).catch(() => []),
      ]);
      setTemplates(tpls);
      setEmployees(emps.filter((e) => e.status === "active"));
      if (!previewEmpId && emps.length > 0) setPreviewEmpId(emps[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [tenantId]);

  // When user switches kind, load that template into the editor
  useEffect(() => {
    const t = templates.find((x) => x.key === kind);
    if (t) {
      setTitle(t.title || "");
      setBodyHtml(t.body || "");
      if (editorRef.current) editorRef.current.innerHTML = t.body || "";
    }
  }, [kind, templates]);

  const runPreview = async () => {
    if (!previewEmpId) return toast.error("Pick an employee for the preview");
    const globalCtx = Object.fromEntries(customVars.filter((r) => r.k && r.v).map((r) => [r.k, r.v]));
    try {
      const { data } = await api.post("/letter-templates/preview", { ...params, title, body: bodyHtml, employee_id: previewEmpId, context: globalCtx });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { const t = setTimeout(runPreview, 500); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [previewEmpId, title, bodyHtml, customVars]);

  const loadUploaded = (data) => {
    setTitle(data.title || title);
    setBodyHtml(data.body);
    if (editorRef.current) editorRef.current.innerHTML = data.body;
    const custom = data.detected_fields.filter((f) => !f.auto && !customVars.some((r) => r.k === f.key)).map((f) => ({ k: f.key, v: "" }));
    if (custom.length) setCustomVars([...customVars, ...custom]);
  };

  const save = async () => {
    if (!title) return toast.error("Title is required");
    setSaving(true);
    try {
      await api.post("/letter-templates", { ...params, key: kind, title, body: bodyHtml });
      toast.success("Template saved");
      await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    if (editorRef.current) setBodyHtml(editorRef.current.innerHTML);
  };
  const insertVar = (key) => {
    const chip = `{{ ${key} }}`;
    const ed = editorRef.current;
    if (!ed) return;
    const sel = window.getSelection();
    const inside = sel && sel.rangeCount > 0 && ed.contains(sel.getRangeAt(0).startContainer);
    if (inside) {
      ed.focus();
      document.execCommand("insertText", false, chip);
    } else {
      ed.innerHTML += (ed.innerHTML && !ed.innerHTML.endsWith("<br>") ? "<br/>" : "") + chip;
    }
    setBodyHtml(ed.innerHTML);
  };
  const onEditorInput = (e) => setBodyHtml(e.currentTarget.innerHTML);

  const generateAI = async () => {
    const subj = window.prompt("What kind of letter should the AI draft? (e.g. 'increment 15% effective April')");
    if (!subj) return;
    try {
      const { data } = await api.post("/admin/ai-generate", { kind: "letter", subject: subj });
      if (editorRef.current) editorRef.current.innerHTML = data.content;
      setBodyHtml(data.content);
      toast.success("AI draft loaded — edit as needed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="letter-studio-page">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-adb-navy">Documents · Studio</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Letter template studio</h1>
        <p className="text-sm text-zinc-500 mt-2">Edit any HR letter template with rich formatting. Pick a real employee to see the letter render live with their data.</p>
      </div>

      {/* Template + employee pickers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Template</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="rounded-sm" data-testid="studio-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(KIND_LABELS).map(([k, label]) => (
                <SelectItem key={k} value={k}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Preview against employee</Label>
          <Select value={previewEmpId} onValueChange={setPreviewEmpId}>
            <SelectTrigger className="rounded-sm" data-testid="studio-preview-emp"><SelectValue placeholder="Pick employee" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">&nbsp;</Label>
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="btn-zenith rounded-sm flex-1" data-testid="studio-save">
              <FloppyDisk size={16} className="mr-1.5" />
              {saving ? "Saving…" : "Save template"}
            </Button>
            <Button variant="outline" onClick={generateAI} className="rounded-sm" data-testid="studio-ai" title="Ask AI to draft">
              <Sparkle size={16} />
            </Button>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <UploadTemplateButton onLoaded={loadUploaded} />
        <CtcTableDialog params={params} onInsert={() => insertVar("ctc_table")} />
        <span className="text-xs text-zinc-500">Upload your own .docx/.txt letter — fields like <code className="font-mono">[Name]</code>, <code className="font-mono">&lt;&lt;Position&gt;&gt;</code> or <code className="font-mono">{"{{ctc_table}}"}</code> are detected and mapped automatically.</span>
      </div>

      {/* Two-pane editor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT: editor */}
        <div className="border border-zinc-200 rounded-sm bg-white flex flex-col">
          <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 flex flex-wrap items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => exec("bold")} className="rounded-sm h-8 w-8 p-0" title="Bold"><TextB size={16} weight="bold" /></Button>
            <Button variant="ghost" size="sm" onClick={() => exec("italic")} className="rounded-sm h-8 w-8 p-0" title="Italic"><TextItalic size={16} /></Button>
            <Button variant="ghost" size="sm" onClick={() => exec("underline")} className="rounded-sm h-8 w-8 p-0" title="Underline"><TextUnderline size={16} /></Button>
            <div className="w-px h-6 bg-zinc-300 mx-1" />
            <Button variant="ghost" size="sm" onClick={() => exec("formatBlock", "<h3>")} className="rounded-sm h-8 w-8 p-0" title="Heading"><TextH size={16} /></Button>
            <Button variant="ghost" size="sm" onClick={() => exec("formatBlock", "<p>")} className="rounded-sm h-8 w-8 p-0" title="Paragraph"><TextAa size={16} /></Button>
            <div className="w-px h-6 bg-zinc-300 mx-1" />
            <Button variant="ghost" size="sm" onClick={() => exec("insertUnorderedList")} className="rounded-sm h-8 w-8 p-0" title="Bullet list"><ListBullets size={16} /></Button>
            <Button variant="ghost" size="sm" onClick={() => exec("insertOrderedList")} className="rounded-sm h-8 w-8 p-0" title="Numbered list"><ListNumbers size={16} /></Button>
            <div className="w-px h-6 bg-zinc-300 mx-1" />
            <input type="color" onChange={(e) => exec("foreColor", e.target.value)} className="h-8 w-8 rounded-sm border border-zinc-300 cursor-pointer" title="Text color" />
          </div>
          <div className="px-3 py-2 border-b border-zinc-200">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-sm h-8 mt-1 font-semibold" data-testid="studio-title-input" />
          </div>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={onEditorInput}
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
            className="p-4 flex-1 min-h-[420px] max-h-[560px] overflow-y-auto text-sm leading-6 text-zinc-800 focus:outline-none"
            style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
            data-testid="studio-editor"
          />
          {/* Variable palette */}
          <div className="px-3 py-3 border-t border-zinc-200 bg-zinc-50 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Insert variable</div>
            <div className="flex flex-wrap gap-1">
              {AUTO_VARS.map((v) => (
                <button key={v.key} onMouseDown={(e) => e.preventDefault()} onClick={() => insertVar(v.key)} title={v.desc} className="text-[10px] font-mono px-2 py-1 rounded-sm border border-zinc-300 bg-white hover:border-adb-navy hover:bg-adb-navy/5" data-testid={`insert-var-${v.key}`}>
                  {"{{ " + v.key + " }}"}
                </button>
              ))}
              <button onMouseDown={(e) => e.preventDefault()} onClick={() => insertVar("ctc_table")} title="Formula-driven CTC table (configure via the CTC table button)" className="text-[10px] font-mono px-2 py-1 rounded-sm border border-adb-navy bg-adb-navy text-white hover:opacity-90" data-testid="insert-var-ctc_table">
                {"{{ ctc_table }}"}
              </button>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold pt-1">Custom (edit below)</div>
            <div className="flex flex-wrap gap-1">
              {CUSTOM_VARS.map((v) => (
                <button key={v.key} onMouseDown={(e) => e.preventDefault()} onClick={() => insertVar(v.key)} title={v.desc} className="text-[10px] font-mono px-2 py-1 rounded-sm border border-emerald-300 bg-emerald-50 hover:border-emerald-500">
                  {"{{ " + v.key + " }}"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: live preview */}
        <div className="border border-zinc-200 rounded-sm bg-white flex flex-col">
          <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
              <Eye size={13} /> Live preview
            </div>
            <Button variant="ghost" size="sm" onClick={runPreview} className="rounded-sm h-7 text-xs" data-testid="studio-refresh-preview">
              <ArrowsClockwise size={13} className="mr-1" /> Refresh
            </Button>
          </div>
          <div className="p-6 flex-1 min-h-[420px] max-h-[560px] overflow-y-auto">
            {preview ? (
              <>
                <div className="text-xl font-display font-bold text-adb-navy mb-4">{preview.title}</div>
                <div className="text-sm leading-7 text-zinc-800 [&_table]:w-full [&_table]:my-3 [&_td]:py-1 [&_td]:px-2 [&_th]:py-1 [&_th]:px-2 [&_th]:bg-zinc-100 [&_td]:border [&_td]:border-zinc-200 [&_th]:border [&_th]:border-zinc-200" dangerouslySetInnerHTML={{ __html: preview.body_html }} style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }} />
              </>
            ) : (
              <div className="text-zinc-500 text-sm text-center py-16">Pick a template and employee to see the preview</div>
            )}
          </div>
        </div>
      </div>

      {/* Custom variable editor */}
      <div className="border border-zinc-200 rounded-sm p-4 bg-white space-y-3">
        <div className="text-sm font-semibold">Sample values for custom variables</div>
        <div className="text-xs text-zinc-500">These values are used only in this preview. When issuing bulk letters, you'll enter real values in the Bulk letters flow.</div>
        <div className="space-y-2">
          {customVars.map((row, i) => (
            <div key={i} className="flex gap-2">
              <Input value={row.k} onChange={(e) => { const next = [...customVars]; next[i].k = e.target.value; setCustomVars(next); }} placeholder="variable_name" className="rounded-sm max-w-xs font-mono text-xs" />
              <Input value={row.v} onChange={(e) => { const next = [...customVars]; next[i].v = e.target.value; setCustomVars(next); }} placeholder="value" className="rounded-sm" />
              <Button variant="outline" size="icon" onClick={() => setCustomVars(customVars.filter((_, x) => x !== i))} className="rounded-sm"><Trash size={14} /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setCustomVars([...customVars, { k: "", v: "" }])} className="rounded-sm">Add variable</Button>
        </div>
      </div>
    </div>
  );
}
